// Sync engine: pushes local changes, pulls remote ones, and listens for
// live change notifications over SSE. Works happily offline — local writes
// queue up as dirty records and flow out when the server is reachable.

import type { PullResponse, PushResponse } from '../domain/sync.ts';
import type { LocalDB } from './idb.ts';
import type { Store } from './store.ts';

export type SyncState =
  | { kind: 'local-only' }
  | { kind: 'needs-key' }
  | { kind: 'connecting' }
  | { kind: 'synced'; at: number; pending: number }
  | { kind: 'offline'; since: number; pending: number }
  | { kind: 'error'; message: string };

export interface SyncConfig {
  /** Base URL, '' for same origin. */
  serverUrl: string;
  key?: string;
}

const PUSH_BATCH = 200;
const POLL_MS = 60_000;

export class SyncEngine {
  private state: SyncState = { kind: 'connecting' };
  private listeners = new Set<() => void>();
  private source?: EventSource;
  private timer?: ReturnType<typeof setTimeout>;
  private poll?: ReturnType<typeof setInterval>;
  private running = false;
  private again = false;
  private lastSeq = 0;
  private epoch?: string;
  private stopped = false;
  private unsubDirty?: () => void;

  private store: Store;
  private db: LocalDB;
  private config: SyncConfig;
  private fetchImpl: typeof fetch;

  constructor(store: Store, db: LocalDB, config: SyncConfig, fetchImpl: typeof fetch = (...args) => fetch(...args)) {
    this.store = store;
    this.db = db;
    this.config = config;
    this.fetchImpl = fetchImpl;
  }

  get status(): SyncState {
    return this.state;
  }

  subscribe = (l: () => void) => {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  };

  private set(state: SyncState) {
    this.state = state;
    for (const l of this.listeners) l();
  }

  async start(): Promise<void> {
    this.stopped = false;
    const savedSeq = await this.db.getMeta<{ seq: number; server: string; epoch?: string }>('sync');
    // If the server or key changed, start pulling from scratch.
    const same = savedSeq && savedSeq.server === this.serverId();
    this.lastSeq = same ? savedSeq.seq : 0;
    this.epoch = same ? savedSeq.epoch : undefined;

    if (!this.config.key) {
      const reachable = await this.health();
      this.set(reachable ? { kind: 'needs-key' } : { kind: 'local-only' });
      return;
    }
    this.unsubDirty = this.store.onDirty(() => this.schedule(400));
    window.addEventListener('online', this.onOnline);
    document.addEventListener('visibilitychange', this.onVisible);
    this.poll = setInterval(() => this.schedule(0), POLL_MS);
    this.openStream();
    await this.syncNow();
  }

  stop(): void {
    this.stopped = true;
    this.unsubDirty?.();
    this.source?.close();
    clearTimeout(this.timer);
    clearInterval(this.poll);
    window.removeEventListener('online', this.onOnline);
    document.removeEventListener('visibilitychange', this.onVisible);
  }

  private onOnline = () => this.schedule(0);
  private onVisible = () => {
    if (document.visibilityState === 'visible') this.schedule(0);
  };

  schedule(ms: number): void {
    if (this.stopped || !this.config.key) return;
    clearTimeout(this.timer);
    this.timer = setTimeout(() => void this.syncNow(), ms);
  }

  async syncNow(): Promise<void> {
    if (!this.config.key) return;
    if (this.running) {
      this.again = true;
      return;
    }
    this.running = true;
    try {
      do {
        this.again = false;
        await this.push();
        await this.pull();
      } while (this.again && !this.stopped);
      if (this.stopped) return;
      this.set({ kind: 'synced', at: Date.now(), pending: this.store.dirtyRecords().length });
    } catch (err) {
      if (this.stopped) return;
      const pending = this.store.dirtyRecords().length;
      if (err instanceof AuthError) this.set({ kind: 'error', message: 'The household key was rejected. Check it in Settings.' });
      else this.set({ kind: 'offline', since: this.state.kind === 'offline' ? this.state.since : Date.now(), pending });
      this.schedule(15_000);
    } finally {
      this.running = false;
    }
  }

  private async push(): Promise<void> {
    const dirty = this.store.dirtyRecords();
    for (let i = 0; i < dirty.length; i += PUSH_BATCH) {
      const batch = dirty.slice(i, i + PUSH_BATCH);
      const body = JSON.stringify({ records: batch.map(({ key: _k, dirty: _d, ...r }) => r) });
      if (this.stopped) return;
      const res = await this.request<PushResponse>('/api/sync', { method: 'POST', body, headers: { 'Content-Type': 'application/json' } });
      if (this.stopped) return;
      await this.store.markClean(batch);
      if (res.restore?.length || res.drop?.length) await this.store.rollBack(res.restore ?? [], res.drop ?? []);
    }
  }

  private async pull(): Promise<void> {
    let more = true;
    while (more && !this.stopped) {
      const res = await this.request<PullResponse>(`/api/sync?since=${this.lastSeq}&limit=1000`);
      if (this.stopped) return;
      // The server database was replaced or restored: start again from zero
      // and re-send everything we have so nothing is lost.
      if ((res.epoch && this.epoch && res.epoch !== this.epoch) || res.seq < this.lastSeq) {
        this.epoch = res.epoch;
        this.lastSeq = 0;
        await this.store.markAllDirty();
        this.again = true;
        return;
      }
      this.epoch = res.epoch;
      await this.store.applyRemote(res.records);
      this.lastSeq = res.seq;
      more = res.more;
      await this.db.setMeta('sync', { seq: this.lastSeq, server: this.serverId(), epoch: this.epoch });
    }
  }

  private openStream(): void {
    if (typeof EventSource === 'undefined' || !this.config.key) return;
    this.source?.close();
    const url = `${this.config.serverUrl}/api/stream?key=${encodeURIComponent(this.config.key)}`;
    const es = new EventSource(url);
    es.addEventListener('change', (ev) => {
      try {
        const { seq } = JSON.parse((ev as MessageEvent).data) as { seq: number };
        if (seq > this.lastSeq) this.schedule(0);
      } catch {
        /* ignore */
      }
    });
    es.onopen = () => this.schedule(0);
    this.source = es;
  }

  private async health(): Promise<boolean> {
    try {
      const res = await this.fetchImpl(`${this.config.serverUrl}/api/health`, { cache: 'no-store' });
      if (!res.ok) return false;
      const body = (await res.json()) as { ok?: boolean };
      return body.ok === true;
    } catch {
      return false;
    }
  }

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const res = await this.fetchImpl(`${this.config.serverUrl}${path}`, {
      ...init,
      cache: 'no-store',
      headers: { ...(init.headers as Record<string, string>), Authorization: `Bearer ${this.config.key}` },
    });
    if (res.status === 401) throw new AuthError();
    if (!res.ok) throw new Error(`Sync failed: ${res.status}`);
    return (await res.json()) as T;
  }

  private serverId(): string {
    return `${this.config.serverUrl}|${this.config.key ?? ''}`;
  }
}

class AuthError extends Error {}
